import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, StarIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import KeyBenefitCard from '../components/KeyBenefitCard';
import KeyBenefitModal from '../components/KeyBenefitModal';
import ConfirmDialog from '../components/ConfirmDialog';

const RatingCertificateValuePage = () => {
    const [benefits, setBenefits] = useState([]);
    const [settings, setSettings] = useState({
        section_title: 'Why A Credit Rating Certificate Matters',
        section_subtitle: ''
    });
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBenefit, setEditingBenefit] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, benefitId: null });

    const LOAN_TYPE = 'rating_certificate_value';

    // Fetch benefits and settings
    const fetchData = async () => {
        try {
            setLoading(true);
            const [benefitsRes, settingsRes] = await Promise.all([
                axios.get(`/api/admin/key-benefits/${LOAN_TYPE}`),
                axios.get(`/api/admin/key-benefits/settings/${LOAN_TYPE}`)
            ]);

            setBenefits(benefitsRes.data.data || []);
            if (settingsRes.data.data) {
                setSettings(settingsRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load Value section data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle create/update
    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);
            if (editingBenefit) {
                await axios.put(`/api/admin/key-benefits/${editingBenefit.id}`, formData);
                toast.success('Value Point updated successfully');
            } else {
                await axios.post('/api/admin/key-benefits', formData);
                toast.success('Value Point created successfully');
            }
            setIsModalOpen(false);
            setEditingBenefit(null);
            fetchData();
        } catch (error) {
            console.error('Error saving benefit:', error);
            toast.error('Failed to save Value Point');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, benefitId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/key-benefits/${deleteConfirm.benefitId}`);
            toast.success('Value Point deleted successfully');
            setDeleteConfirm({ isOpen: false, benefitId: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting Value Point:', error);
            toast.error('Failed to delete Value Point');
        }
    };

    // Handle settings update
    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put(`/api/admin/key-benefits/settings/${LOAN_TYPE}`, settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Understanding the Value</h1>
                    <p className="text-slate-500 mt-1">Manage the "Why It Matters" section of Rating Certificate</p>
                </div>
                <button
                    onClick={() => {
                        setEditingBenefit(null);
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add Value Point
                </button>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4">Section Header Settings</h2>
                <div className="grid grid-cols-1 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Title
                        </label>
                        <input
                            type="text"
                            value={settings.section_title}
                            onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Why A Credit Rating Certificate Matters"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Content (Introduction Text)
                        </label>
                        <textarea
                            value={settings.section_subtitle}
                            onChange={(e) => setSettings({ ...settings, section_subtitle: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent h-32"
                            placeholder="Enter the introductory paragraphs here..."
                        />
                        <p className="text-xs text-slate-400 mt-1">This text will be displayed below the title. You can separate paragraphs with double newlines.</p>
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleSettingsSave}
                        disabled={savingSettings}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                    >
                        {savingSettings ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* Benefits Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white rounded-2xl h-64 shadow-sm animate-pulse border border-slate-100"></div>
                    ))}
                </div>
            ) : benefits.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-slate-100">
                    <div className="max-w-md mx-auto">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <ShieldExclamationIcon className="h-8 w-8 text-slate-400" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">No value points added yet</h3>
                        <p className="text-slate-500 mb-6">Create points to showcase the value of the certificate.</p>
                        <button
                            onClick={() => {
                                setEditingBenefit(null);
                                setIsModalOpen(true);
                            }}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg"
                        >
                            Add First Point
                        </button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {benefits.map((benefit) => (
                        <KeyBenefitCard
                            key={benefit.id}
                            benefit={benefit}
                            onEdit={(b) => {
                                setEditingBenefit(b);
                                setIsModalOpen(true);
                            }}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {/* Modal */}
            <KeyBenefitModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingBenefit(null);
                }}
                onSubmit={handleSubmit}
                benefit={editingBenefit}
                loading={submitting}
                loanType={LOAN_TYPE}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, benefitId: null })}
                onConfirm={confirmDelete}
                title="Delete Value Point"
                message="Are you sure you want to delete this point? This action cannot be undone."
            />
        </div>
    );
};

export default RatingCertificateValuePage;
